/**
 * Structured-data builders for the tutorials hub — the AEO/GEO payload. Each tutorial page emits an
 * Article + HowTo (from steps) + FAQPage (from faq) + BreadcrumbList so search + answer/generative
 * engines can lift the direct answer, the steps, and the Q&A. Pure functions → unit-testable.
 */
import type { Tutorial } from './tutorials';
import type { Author } from './authors';

const ORIGIN = 'https://wisetree.ai';
const publisher = { '@type': 'Organization', name: 'Wisetree AI Pty Ltd', url: `${ORIGIN}/` };

export const tutorialUrl = (slug: string): string => `${ORIGIN}/tutorials/${slug}`;
export const authorUrl = (slug: string): string => `${ORIGIN}/authors/${slug}`;

/** The full JSON-LD array for a tutorial page (Article + HowTo + FAQPage + BreadcrumbList). */
export function tutorialJsonLd(t: Tutorial, author?: Author): Record<string, unknown>[] {
  const url = tutorialUrl(t.slug);
  const authorNode = author
    ? { '@type': 'Person', name: author.name, url: authorUrl(author.slug), description: author.tagline }
    : { '@type': 'Organization', name: 'Wisetree AI' };

  const article = {
    '@context': 'https://schema.org', '@type': 'Article',
    headline: t.title, description: t.metaDescription, abstract: t.tldr,
    dateModified: t.updated, datePublished: t.updated,
    mainEntityOfPage: url, author: authorNode, publisher,
    keywords: t.keyTerms.map((k) => k.term).join(', '),
  };

  const howTo = {
    '@context': 'https://schema.org', '@type': 'HowTo',
    name: t.title, description: t.tldr,
    step: t.steps.map((s, i) => ({ '@type': 'HowToStep', position: i + 1, name: s.title, text: s.detail })),
  };

  const faqPage = t.faq.length
    ? {
        '@context': 'https://schema.org', '@type': 'FAQPage',
        mainEntity: t.faq.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
      }
    : null;

  const breadcrumb = {
    '@context': 'https://schema.org', '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${ORIGIN}/` },
      { '@type': 'ListItem', position: 2, name: 'Tutorials', item: `${ORIGIN}/tutorials` },
      { '@type': 'ListItem', position: 3, name: t.title, item: url },
    ],
  };

  return [article, howTo, ...(faqPage ? [faqPage] : []), breadcrumb];
}

/** JSON-LD for an author profile page (Person + their tutorials as a ProfilePage). */
export function authorJsonLd(author: Author, tutorialSlugs: { slug: string; title: string }[]): Record<string, unknown> {
  return {
    '@context': 'https://schema.org', '@type': 'ProfilePage',
    mainEntity: {
      '@type': 'Person', name: author.name, description: author.bio, jobTitle: author.tagline,
      url: authorUrl(author.slug), knowsAbout: author.expertise, worksFor: publisher,
    },
    hasPart: tutorialSlugs.map((t) => ({ '@type': 'Article', headline: t.title, url: tutorialUrl(t.slug) })),
  };
}

/** JSON-LD for the tutorials index (CollectionPage + ItemList). */
export function tutorialsIndexJsonLd(items: { slug: string; title: string }[]): Record<string, unknown> {
  return {
    '@context': 'https://schema.org', '@type': 'CollectionPage',
    name: 'Wisetree AI Tutorials',
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: items.map((t, i) => ({ '@type': 'ListItem', position: i + 1, url: tutorialUrl(t.slug), name: t.title })),
    },
  };
}
